import React, { FC, useState, useEffect, useRef, MutableRefObject } from 'react';
import { ContactUsFooterProps, ContactUsFooterModel } from './ContactUsFooter.types';
import { StyledContactUsFooter, StyledFooter } from './ContactUsFooter.styles';
import { Button, Icon, Link, useCheckBreakpoint, Image } from '@marriott/mi-ui-library';
import DOMPurify from 'isomorphic-dompurify';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { useDCADynamicModelAlloy, getDirection } from '../../modules/helpers';
import { LanguageSelector } from '../LanguageSelector';
import { MOBILE_NAVBAR_HEIGHT, NAVBAR_HEIGHT } from '../../modules/constants/constant';

export const ContactUsFooter: FC<ContactUsFooterProps> = (props: ContactUsFooterProps) => {
  const isMobileViewPort = !useCheckBreakpoint('viewportM');
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false);
  const [direction, setDirection] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const qrRef = useRef(null) as unknown as MutableRefObject<HTMLDivElement | null>;

  useEffect(() => {
    setDirection(getDirection());
  }, []);

  //Subtracting the scroll bar width from the viewport when language selector is opened to avoid horizontal jumpy behavior
  useEffect(() => {
    if (isLanguageSelectorOpen) {
      if (document.body.style.position === 'fixed') {
        const currentTop = parseInt(document.body.style.top || '0', 10);
        document.body.style.top = `${currentTop - (isMobileViewPort ? MOBILE_NAVBAR_HEIGHT : NAVBAR_HEIGHT)}px`;
      }
    } else {
      // Reset top if previously adjusted
      if (document.body.style.top) {
        document.body.style.top = '';
      }
    }
  }, [isLanguageSelectorOpen]);

  useEffect(() => {
    const handleClickOutsideQrcode = (event: MouseEvent) => {
      if (qrRef?.current && !qrRef?.current?.contains(event.target as Node)) setShowQrCode(false);
    };
    //when we click outside the qr code image, it should close
    if (showQrCode) document.addEventListener('click', handleClickOutsideQrcode);
    return () => {
      document.removeEventListener('click', handleClickOutsideQrcode);
    };
  }, [showQrCode]);

  const toggleHandler = () => {
    setIsLanguageSelectorOpen(!isLanguageSelectorOpen);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClickOnSelectorButton = (event: any) => {
    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
      toggleHandler();
    }
  };

  //show qr code
  const handleShowQrCode = (isqrCode: boolean) => {
    isqrCode && setShowQrCode(true);
  };

  //hide qr code
  const handleHideQrCode = (event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event?.stopPropagation();
    setShowQrCode(false);
  };

  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const model: ContactUsFooterModel = targetData ? targetData : props?.model;

  return (
    <StyledContactUsFooter
      data-testid="luxury-ContactUsFooter"
      data-component-name="m-luxury-ContactUsFooter"
      className="m-container-fullbleed"
    >
      <div className="container color-scheme2 dark-theme px-3 px-sm-0">
        {model?.showContactUs && (
          <div className="header-text container px-0">
            <div className="row luxury-header-text-cta">
              <div className="col-xl-8 col-md-6 col-12 pr-3 pl-0">
                {model.headerPrimaryHOneTag && (
                  <h1 className="header-text-heading p-0 col-md-12 mb-md-0 col-12 t-title-l mb-3">
                    {model?.headerText}
                  </h1>
                )}
                {!model.headerPrimaryHOneTag && (
                  <div className="header-text-heading p-0 col-md-12 mb-md-0 col-12 t-title-l mb-3">
                    {model.headerText}
                  </div>
                )}
              </div>
              {model?.iconDetails.length >= 0 && (
                <div className="col-12 col-xl-4 col-md-6 pl-0 pl-sm-2 pr-0">
                  {model?.iconDetails.map((contactItem, index) => {
                    return (
                      <div
                        className={clsx(
                          'p-0 col-md-12 col-12 t-font-m mr-4',
                          index === model?.iconDetails.length - 1 ? '' : 'mb-3'
                        )}
                        key={index}
                      >
                        <span className={`mr-2 ${contactItem.icon}`}></span>
                        <Link
                          text={contactItem.iconText ?? ''}
                          linkClassName="pb-3 m-0 link-underline t-font-m"
                          ariaLabel={contactItem.ariaLabel}
                          linkHref={
                            contactItem.iconType === 'tel'
                              ? `tel:${contactItem.iconText ?? ''}`
                              : `mailto:${contactItem.iconText ?? ''}`
                          }
                          target="_self"
                          linkType="internal"
                        >
                          <span className="sr-only">{contactItem.iconText}</span>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {model?.socialIcons?.length >= 1 && (
          <div className="row mt-social mt-4 px-0">
            <span className="t-font-m mt-social__title">{model?.socialIconsTitle}</span>
            <ul className="mt-social__icons">
              {model?.socialIcons.map((socialItem, index) => {
                return (
                  <li className="mr-4" key={index}>
                    <Link
                      linkClassName="t-font-alt-md"
                      linkHref={socialItem.linkUrl as string}
                      custom_click_track_value={socialItem.clickTrackValue}
                      ariaLabel={socialItem.linkText}
                      target={socialItem.openInANewTab ? '_blank' : '_self'}
                    >
                      <span
                        className={`${socialItem.icon}`}
                        onClick={() => handleShowQrCode(socialItem.linkToQrImage)}
                        ref={socialItem.icon === 'icon-wechat' ? qrRef : null}
                      >
                        {socialItem.linkToQrImage && showQrCode && (
                          <div className="cn-qrcode">
                            <div className="close_icon icon-clear" onClick={handleHideQrCode}></div>
                            <div className="qrcode_title">{socialItem.qrTitle}</div>
                            <div className="qrcode_img">
                              <Image
                                defaultImageURL={socialItem?.dynamicImage?.assetPath}
                                altText={socialItem?.dynamicImage?.altText}
                                renditions={socialItem?.dynamicImage?.renditions}
                              />
                            </div>
                          </div>
                        )}
                      </span>
                      <span className="sr-only">{socialItem.linkText}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <div className="dark-theme mt-copyright-component">
          <div
            className={clsx(
              'mt-copyright-component__copyright t-font-xs py-4 d-flex',
              isMobileViewPort ? 'flex-column-reverse' : 'flex-row'
            )}
          >
            <div
              className="mt-copyright-component__copyright-text"
              dangerouslySetInnerHTML={{
                __html: (
                  DOMPurify.sanitize(model?.copyrightInfo as string, {
                    RETURN_DOM: true,
                  }) as HTMLElement
                ).outerHTML,
              }}
            ></div>
            {model?.showLanguageSelector && props?.languageSelectorModal && (
              <div
                className="language-selector-button-footer d-flex flex-row align-items-center mt-md-0 inverse mt-copyright-component__language-link mb-3"
                tabIndex={0}
                onKeyDown={handleClickOnSelectorButton}
                role="button"
              >
                <Button
                  isLink={true}
                  className="language-selector-button t-font-xs custom_click_track"
                  callback={toggleHandler}
                  ariaLabel={model?.currentLanguage}
                  ariaRole="button"
                  custom_click_track_value={model?.clickTrackValue ?? ''}
                >
                  <Icon iconClass={clsx('icon-globe', 'icon-inverse', 'icon-s')} />
                  <span aria-hidden="true" className="pl-1">
                    {model?.currentLanguage}
                  </span>
                </Button>
              </div>
            )}
          </div>
          {model?.showLanguageSelector && props?.languageSelectorModal && (
            <div className="language-selector-button">
              <LanguageSelector
                currentLanguage={model?.currentLanguage}
                clickTrackValue={model?.clickTrackValue}
                toggleHandler={toggleHandler}
                setIsOpen={setIsLanguageSelectorOpen}
                isOpen={isLanguageSelectorOpen}
                languageSelectorModal={props?.languageSelectorModal}
              />
            </div>
          )}
        </div>

        <StyledFooter
          id="footer-wrapper-container"
          className="footer-wrapper"
          data-testid="footer"
          data-component-name="luxury-footer"
        >
          <div className="col-12 pl-0">
            <div className="inner-container pb-4">
              <div className="footer-bottom-link-list mb-4">
                <div className="d-flex flex-row">
                  <span id="teconsent" className="mt-multi-link__link"></span>
                  <span className={clsx(direction ? 'icon-arrow-left' : 'icon-arrow-right')}></span>
                </div>
                {model?.footerLinks?.map((footerLink, index) => (
                  <React.Fragment key={`footer-link-fragment-${index}`}>
                    <div className="footer-link-wrapper">
                      <Link
                        key={`footer-link-${index}`}
                        text={footerLink.ctaProperties.linkText}
                        linkClassName={'footer-link t-font-xs custom_click_track'}
                        ariaLabel={footerLink.ctaProperties.linkText}
                        linkHref={footerLink.ctaProperties.linkUrl}
                        linkType="internal"
                        target={footerLink.ctaProperties.openInANewTab ? '_blank' : '_self'}
                        custom_click_track_value={footerLink.clickTrackValue}
                      ></Link>
                      <span
                        key={`arrow-icon-${index}`}
                        className={clsx(`mt-1 mt-md-0`, direction ? 'icon-arrow-left' : 'icon-arrow-right')}
                      ></span>
                    </div>
                  </React.Fragment>
                ))}
                <div className="d-flex flex-row">
                  <span id="ccpalink" className="mt-multi-link__link"></span>
                  <span className={clsx(direction ? 'icon-arrow-left' : 'icon-arrow-right')}></span>
                </div>
              </div>
              <div className="session-details">
                <div className="js-session-detail t-font-xs" aria-hidden="true"></div>
              </div>
            </div>
          </div>
        </StyledFooter>
      </div>
    </StyledContactUsFooter>
  );
};

export const ContactUsFooterConfig = {
  emptyLabel: 'ContactUsFooter',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/footer/v1/footer`,
};
//TODO:Need to fix the lint issue for editable component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ContactUsFooterEditable = (props: any) => (
  <div data-testid="contact-us-footer" data-component-name="m-contactusfooter">
    <EditableComponent config={ContactUsFooterConfig} {...props}>
      <ContactUsFooter {...props} />
    </EditableComponent>
  </div>
);
